import SettingsTabBase from "./SettingsTabBase.jsx";
import { SETTING_TAB_WIDGET_KEYS, EDIT_MESSAGES } from "../../../other/constants/OtherKeys.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../../other/constants/PersistenceStateKeys.jsx";
import { userPreferences } from "../../../react/context/bryntumContext.jsx";
import { STORE_KEYS } from "../../../other/constants/StoreKeys.jsx";
import { TAGS_KEYS } from "../../../other/constants/TagsKeys.jsx";
import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import { EDITOR_KEYS } from "../../../other/constants/EditorKeys.jsx";
import { StringHelper, NumberFormat } from "@bryntum/schedulerpro";

// NumberFormat without a thousand separators for archive days field
const archiveDaysFormat = new NumberFormat({
  useGrouping: false
});

const MAX_LENGTH_ERROR_MESSAGE = "Archive days cannot be greater than 9999";

export default class SettingsTaskAutoArchiveTab extends SettingsTabBase {
  static get $name() {
    return "SettingsTaskAutoArchiveTab";
  }

  // Factoryable type name
  static get type() {
    return "settingstaskautoarchivetab";
  }

  get swivuDeepLinkSlug() {
    return SETTING_TAB_WIDGET_KEYS.taskAutoArchiveTab;
  }

  get isSettingsTabBase() {
    return true;
  }

  static get configurable() {
    return {
      title: "Auto Archive Tasks",
      cls: "p-[1em] !overflow-y-auto @container gap-[1.2em]",
      defaults: {
        cls: "flex flex-wrap @sm:flex-nowrap gap-3",
        labelWidth: "18em",
        labelCls: "@text-nowrap"
      },
      items: {
        enableAutoArchiveField: {
          type: "checkbox",
          name: "enableAutoArchive",
          text: "Enable Auto Archive",
          checked: false,
          onPaint: "up.onLoadAutoArchiveSetting",
          onChange: "up.onAutoArchiveSettingChange",
        },
        archiveDaysField: {
          type: "number",
          name: "archiveDays",
          label: "Archive Inactive Tasks Days-Older Than",
          min: 1,
          max: 9999,
          step: 1,
          format: archiveDaysFormat,
          onPaint: "up.onLoadAutoArchiveSetting",
          onChange: "up.onAutoArchiveSettingChange",
          onInput: "up.onAutoArchiveSettingDaysInput",
        },
        statusFilterField: {
          type: WIDGET_KEYS.settingsCombo,
          name: "statusFilter",
          label: "Task Status Filter",
          displayField: "tagValue",
          valueField: "id",
          placeholder: "None - All statuses will be considered",
          multiSelect: true,
          hidePickerOnSelect: true,
          editMessage: EDIT_MESSAGES.status,
          tabName: SETTING_TAB_WIDGET_KEYS.taskStatusTab,
          onSwivuEditMessage: "onClickSwivuEditMessage",
          onPaint: "up.onLoadAutoArchiveSetting",
          onChange: "up.onAutoArchiveSettingChange",
        },
        explanationHtml: {
          cls: "ml-0 mt-[1em] p-[1em] bg-[#f8f9fa] dark:bg-[#2a2a2a] border border-[#dee2e6] dark:border-[#444] rounded",
          html: `
            <div class="space-y-3">
              <h4 class="font-semibold text-[#495057] dark:text-[#e9ecef] mb-2 !mt-[0]">What Constitutes An "Inactive" Task?</h4>
              <p class="text-[1em] text-[#6c757d] dark:text-[#adb5bd]">
                A task is considered inactive when it has not been modified for the specified number of days.
                This includes any changes to task properties such as name, description, dates, status, assignments,
                notes, or any other task-related updates. Tasks with no recent activity are candidates for archiving
                to help keep your active task list focused and manageable.
              </p>

              <h4 class="font-semibold text-[#495057] dark:text-[#e9ecef] mb-2 mt-4">How Auto Archive Works:</h4>
              <ul class="text-[1em] text-[#6c757d] dark:text-[#adb5bd] list-disc list-outside pl-6 space-y-1">
                <li><strong>Daily Processing:</strong> Tasks are automatically archived once per day during system maintenance</li>
                <li><strong>First Run Notice:</strong> On initial activation, if there are many tasks to archive, this process may take some time</li>
                 <li><strong>Status Filter:</strong> If specific statuses are selected, only tasks with those statuses will be considered for archiving</li>
                <li><strong>Selective Archiving:</strong> Only tasks meeting both the age criteria and optional status filter will be archived</li>
              </ul>
            </div>
          `
        }
      }
    };
  }

  onAutoArchiveSettingDaysInput() {
    const { archiveDaysField } = this.widgetMap;
    const originalValue = archiveDaysField.value;

    if (archiveDaysField.value > archiveDaysField.max) {
      archiveDaysField.value = Math.floor(originalValue / 10);
      archiveDaysField.setError(MAX_LENGTH_ERROR_MESSAGE);

      if (this.swivuErrorTimer) clearTimeout(this.swivuErrorTimer);
      this.swivuErrorTimer = setTimeout(() => {
        if(!archiveDaysField.isValid) archiveDaysField.clearError(MAX_LENGTH_ERROR_MESSAGE);
      }, 1000);
    }

    /** Only persist the setting if the value was actually changed during validation */
    if (archiveDaysField.value !== originalValue) {
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveDays, archiveDaysField.value, true);
    }
  }

  onAutoArchiveSettingDaysFocusOut({ source }) {
    source.clearError(MAX_LENGTH_ERROR_MESSAGE);
  }

  onLoadAutoArchiveSetting({ source }) {
    this.disabledFieldsForNonOwners();

    switch (source.name) {
      case "enableAutoArchive": {
        const isEnabled = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveEnabled, false, true);
        source.checked = isEnabled === "true" || isEnabled === true;
        this.updateFieldStates(source.checked);
        break;
      }
      case "archiveDays": {
        const archiveDays = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveDays, 365, true);
        source.value = StringHelper.safeJsonParse(archiveDays);
        break;
      }
      case "statusFilter": {
        const taskTagValuesStore = this.project.getCrudStore(STORE_KEYS.taskTagValuesStore);
        source.items = taskTagValuesStore.query(record => record.tagSet === TAGS_KEYS.status, true);

        const statusFilterSetting = userPreferences.getSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveStatusFilter, null, true);
        source.value = statusFilterSetting ? StringHelper.safeJsonParse(statusFilterSetting) : [];
        break;
      }
      default:
        break;
    }
  }

  loadRecord(record, context, otherParams) {
    super.loadRecord(record, context, otherParams);
    this.context = context;
  }

  onAutoArchiveSettingChange({ source, userAction, value }) {
    if (userAction) {
      switch (source.name) {
        case "enableAutoArchive":
          userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveEnabled, String(source.checked), true);
          this.updateFieldStates(source.checked);
          break;
        case "archiveDays": {
          let val = value;
          if (value < source.min) {
            val = source.min;
            source.value = source.min
          };
          userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveDays, val, true);
          break;
        }
        case "statusFilter": {
          let statusFilterValue = StringHelper.safeJsonStringify(value);
          userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.taskAutoArchiveStatusFilter, statusFilterValue, true);
          break;
        }
        default:
          break;
      }
    }
  }

  updateFieldStates(isEnabled) {
    const { archiveDaysField, statusFilterField } = this.widgetMap;
    archiveDaysField.disabled = !isEnabled;
    statusFilterField.disabled = !isEnabled;
  }

  disabledFieldsForNonOwners() {
    const isOwner = this.context?.selectedBoard?.record?.isOwner;
    if (!isOwner) {
      const { enableAutoArchiveField, archiveDaysField, statusFilterField } = this.widgetMap;
      enableAutoArchiveField.disabled = true;
      archiveDaysField.disabled = true;
      statusFilterField.disabled = true;
    }
  }

  onClickSwivuEditMessage({ tabName }) {
    this.editorHandler.openEditor(EDITOR_KEYS.settingGeneralEditor, null, { selectTabByTypeOnOpen: tabName }, { by: "recordType", useExisting: true });
  }
}

SettingsTaskAutoArchiveTab.initClass();
